
import { indexDbConsulta } from '../../../shared/helpers/indexDb';
import StringHelper from '../../../shared/helpers/StringHelper';
import { getSearch } from '../apis/AccessibilityApi';
import { APIHelper, ToastifyHelper, Permissao } from '../../../shared/helpers';
import { CancelToken } from 'axios';

export type ExpiresType = { expires: number; search_id: string };
export type FilterItem = {
    code: string;
    description: string;
};

export type FilterType = {
    typeOfWork: FilterItem[];
    libraries: FilterItem[];
};

export type LinkType = {
    seq_paragrafo: string;
    tipo_link: string;
    descricao: string;
    url_link: string;
    is_pdf: boolean;
};
export interface BookType {
    cod_empresa: string;
    cod_acervo: string;
    cod_autoridade: string | null;
    descricao: string;
    obra: string;
    desc_tipo_obra: string;
    cod_tipo_obra: string;
    referencia: string;
    referencia_sem_tag: string;
    gera_exemplar: string;
    classificacao: string;
    tot_exemplares: string;
    total_titulos: string;
    link_capa: string;
    gera_773: string;
    tipo_p: string;
    possui_doc_online: string;
    possui_out_titulos: string;
    tem_773_relacionado: string;
    arquivo_indexar: string;
    qtde_comentarios: string;
    cod_nivel_permissao: string | null;
    permite_arquivo_vinculo: string;
    permite_arquivo_exemplar: string;
    icone: string;
    cod_sit_acervo: string;
    isbn_issn: string | null;
    resumo: string | null;
    autor: string | null;
    seq_paragrafo_capa: string | null;
    ano_publicacao: string;
    acesso_remoto: string;
    possui_acessibilidade: string | null;
    idioma: string;
    desc_fisica: string | null;
    disp_para_reserva: string | null;
    idioma_desc: string | null;
    autor_principal: string | null;
    edicao: string | null;
    eh_legislacao: string;
    cor: string | null;
    editora_principal: string | null;
    desc_titulo?: string;
    links: LinkType[] | null;
    dados_adicionais: {
        B: { code: string; description: string }[];
        // A: { code: string; description: string }[];
        // E: { code: string; description: string }[];
        // P: { code: string; description: string }[];
        // S: { code: string; description: string }[];
        // C: { code: string; description: string }[];
    };
}

export const getSearchData = async (search_id: string): Promise<BookType[]> => {
    const { getAllBy } = indexDbConsulta;
    return await getAllBy<BookType[]>('consulta', search_id, 'search_id');
};

export const getExpirationData = async (
    search_text: string,
): Promise<ExpiresType> => {
    const { getBy } = indexDbConsulta;
    return await getBy<ExpiresType>('expiracao', search_text);
};

export const checkExpiration = (expiresData: ExpiresType) => {
    const { expires } = expiresData;
    const now = new Date().getTime();
    return expires > now;
};

export const clearTables = async (): Promise<void> => {
    const { clearTables } = indexDbConsulta;
    await clearTables(['consulta', 'expiracao']);
};

export const storeCompleteSearchData = async (
    search_data: BookType[],
): Promise<boolean> => {
    const { insertData } = indexDbConsulta;
    return await insertData('consulta', search_data);
};

export const storeExpiration = async (
    expiration: ExpiresType,
): Promise<boolean> => {
    const { insertData } = indexDbConsulta;
    return await insertData('expiracao', expiration);
};

export const deleteBySearchId = async (search_id: string) => {
    const { deleteAllByIndex } = indexDbConsulta;
    await deleteAllByIndex('consulta', search_id, 'search_id');
    await deleteAllByIndex('expiracao', search_id, 'search_id');
};

export const getFiltersData = async (data: BookType[]): Promise<FilterType> => {
    return new Promise((resolve, reject) => {
        try {
            const filters: FilterType = data.reduce(
                (
                    acumulador: {
                        typeOfWork: FilterItem[];
                        libraries: FilterItem[];
                    },
                    element: any,
                ) => {
                    if (element.dados_adicionais.hasOwnProperty('B')) {
                        const { B }: { B: FilterItem[] } =
                            element.dados_adicionais;
                        if (B.length > 0) {
                            const dataNoAdded = B.filter(
                                b =>
                                    !acumulador.libraries.some(
                                        acm => acm.code === b.code,
                                    ),
                            );
                            acumulador.libraries = [
                                ...acumulador.libraries,
                                ...dataNoAdded,
                            ];
                        }
                    }

                    if (element.dados_adicionais.hasOwnProperty('T')) {
                        const {
                            cod_tipo_obra: code,
                            desc_tipo_obra: description,
                        } = element;
                        if (
                            code &&
                            !acumulador.typeOfWork.some(
                                acm => acm.code === code,
                            )
                        ) {
                            acumulador.typeOfWork.push({
                                code,
                                description: StringHelper.replaceAll(
                                    description,
                                    ['(', ')'],
                                    '',
                                ).trim(),
                            });
                        }
                    }
                    return acumulador;
                },
                { typeOfWork: [], libraries: [] },
            );

            filters.typeOfWork.sort((a, b) =>
                a.description.localeCompare(b.description),
            );
            filters.libraries.sort((a, b) =>
                a.description.localeCompare(b.description),
            );
            resolve(filters);
        } catch (e) {
            reject(e);
        }
    });
};

export const paginate = (
    data: BookType[],
    itemsPerPage: number,
    page: number,
) => {
    const startIndex = 0;
    let endIndex = page * itemsPerPage - 1;

    if (endIndex > data.length) endIndex = data.length;

    return data.slice(startIndex, endIndex);
};

export const loadSearchData = async (
    searchText: string,
    accessibleTitleSearch: 'S' | 'N',
    cancelToken?: CancelToken,
): Promise<BookType[] | false> => {
    let expiresData: ExpiresType | null = null;

    try {
        expiresData = await getExpirationData(
            accessibleTitleSearch === 'N'
                ? searchText.trim()
                : 'accessibilitySearchTitlesData',
        );
        if (expiresData) {
            if (checkExpiration(expiresData)) {
                const { search_id } = expiresData;
                return await getSearchData(search_id);
            }

            throw new Error('data expired');
        }
    } catch (e) {
        console.error(e);
        if (expiresData !== null) {
            await deleteBySearchId(expiresData.search_id);
        }
    }

    try {
        const response = await getSearch(
            searchText.trim(),
            accessibleTitleSearch,
            cancelToken
        );
        if (response) {
            const { data } = response;

            if (data.length > 0) {
                const hours = 1;

                const dataExpires = {
                    expires: new Date().getTime() + hours * 60 * 60 * 1000,
                    search_id: data[0].search_id,
                    search_text:
                        accessibleTitleSearch === 'N'
                            ? searchText.trim()
                            : 'accessibilitySearchTitlesData',
                };

                await indexDbConsulta.insertData('consulta', data);
                await indexDbConsulta.insertData('expiracao', dataExpires);
                return data;
            }
        }

        ToastifyHelper.warning(
            `Nenhum resultado foi  encontrado para essa pesquisa."`,
        );
    } catch (e) {
        APIHelper.tratarErro(e);
    }

    return false;
};

export const processLinks = () => {};
